import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Gem, Search, Filter } from 'lucide-react';

interface AssetsEmptyStateProps {
  type: 'no-assets' | 'no-search-results';
  onAddAsset: () => void;
  onClearFilters?: () => void;
  searchTerm?: string;
  totalAssets?: number;
  onAiImport?: () => void;
}

export function AssetsEmptyState({ 
  type, 
  onAddAsset, 
  onClearFilters, 
  searchTerm,
  totalAssets = 0,
  onAiImport
}: AssetsEmptyStateProps) {
  const navigate = useNavigate();

  if (type === 'no-assets') {
    return (
      <Card className="text-center">
        <CardContent className="pt-8 pb-6 px-6">
          <div className="bg-slate-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
            <Gem className="h-8 w-8 text-[#8B5CF6]" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No Assets Yet</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Add assets of any type manually to start building your investment portfolio.{' '}
            {onAiImport && (
              <>
                Use{' '}
                <button
                  onClick={onAiImport}
                  className="text-primary hover:text-primary/80 underline cursor-pointer font-medium"
                >
                  Ai-Importer
                </button>{' '}
                to automate the task, or{' '}
              </>
            )}
            <button 
              onClick={() => navigate('/investments/connect')}
              className="text-primary hover:text-primary/80 underline cursor-pointer font-medium"
            >
              Connect
            </button>{' '}
            your accounts to automaticly retrieve holdings.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="text-center">
      <CardContent className="pt-8 pb-6 px-6">
        <div className="bg-slate-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
          <Search className="h-8 w-8 text-[#8B5CF6]" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Assets Found</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          {searchTerm 
            ? `No assets match "${searchTerm}". Try adjusting your search or filters.`
            : `No assets match your current filters. Try adjusting your criteria.`
          }
        </p>
        <div className="flex gap-2 justify-center">
          {onClearFilters && (
            <Button variant="outline" onClick={onClearFilters} className="gap-2">
              <Filter className="h-4 w-4" />
              Clear Filters
            </Button>
          )}
        </div>
        {totalAssets > 0 && (
          <p className="text-sm text-muted-foreground mt-4">
            You have {totalAssets} total asset{totalAssets !== 1 ? 's' : ''} in your portfolio
          </p>
        )}
      </CardContent>
    </Card>
  );
}
